import React, { useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import safeToast from "utils/safeToast";

// MDUI Components
import MDBox from "components/MDBox";
import MDTypography from "components/MDTypography";
import MDButton from "components/MDButton";
import Card from "@mui/material/Card";
import Grid from "@mui/material/Grid";
import CircularProgress from "@mui/material/CircularProgress";
import CardContent from "@mui/material/CardContent";
import TextField from "@mui/material/TextField";
import InputLabel from "@mui/material/InputLabel";
import Icon from "@mui/material/Icon";
import Tooltip from "@mui/material/Tooltip";
import Divider from "@mui/material/Divider";
import Accordion from "@mui/material/Accordion";
import AccordionSummary from "@mui/material/AccordionSummary";
import AccordionDetails from "@mui/material/AccordionDetails";
import ExpandMoreIcon from "@mui/icons-material/ExpandMore";

import { API_ENDPOINTS } from "config/api";
import { decryptText } from "config/encryption";

const FamilyInfo = () => {
  const [familyInfo, setFamilyInfo] = useState({
    father: null,
    mother: null,
    spouse: null,
    guardian: null,
  });
  const [loading, setLoading] = useState(true);
  const [editMode, setEditMode] = useState({
    father: false,
    mother: false,
    spouse: false,
    guardian: false,
  });
  const [formData, setFormData] = useState({
    father: {},
    mother: {},
    spouse: {},
    guardian: {},
  });
  const [dropdowns, setDropdowns] = useState({
    genders: [],
  });
  const navigate = useNavigate();

  useEffect(() => {
    const fetchFamilyInfo = async () => {
      try {
        const userDetails = localStorage.getItem("UserLogDetails");
        if (!userDetails) throw new Error("No user details");

        const decryptedUser = decryptText(userDetails);
        const parsedUser = JSON.parse(decryptedUser);
        const { StudentId, Token } = parsedUser;

        if (!Token) throw new Error("Missing token");

        const response = await axios.get(
          API_ENDPOINTS.PROFILE.GET_FAMILY_MEMBERS(StudentId),
          {
            headers: {
              "Content-Type": "application/json",
              Authorization: `Bearer ${Token}`,
            },
          }
        );

        // Organize by category
        const organized = {
          father: response.data.find((m) => m.mnid === 0) || null,
          mother: response.data.find((m) => m.mnid === 1) || null,
          spouse: response.data.find((m) => m.mnid === 3) || null,
          guardian: response.data.find((m) => m.mnid === 2) || null,
        };

        setFamilyInfo(organized);
      } catch (error) {
        if (error.response?.status === 401 || error.message === "Missing token") {
          safeToast.error("Session expired. Please log in again.");
          localStorage.clear();
          navigate("/BasicAuth");
        } else {
          safeToast.error("Failed to load family info.");
        }
      } finally {
        setLoading(false);
      }
    };

    fetchFamilyInfo();
  }, [navigate]);

  const fetchDropdowns = async (Token) => {
    try {
      const dropdownRes = await axios.get(API_ENDPOINTS.PROFILE.GET_DROPDOWNS, {
        headers: {
          Authorization: `Bearer ${Token}`,
        },
      });
      setDropdowns({
        genders: dropdownRes.data.genders || [],
      });
    } catch (error) {
      console.error("Error fetching dropdowns", error);
      throw new Error("Failed to fetch dropdowns");
    }
  };

  const handleEdit = async (type) => {
    try {
      const userDetails = localStorage.getItem("UserLogDetails");
      const decryptedUser = decryptText(userDetails);
      const { Token } = JSON.parse(decryptedUser);

      await fetchDropdowns(Token);

      const member = familyInfo[type];
      if (!member) {
        // Initialize empty form for new entry
        setFormData((prev) => ({
          ...prev,
          [type]: {
            name: "",
            gender: "",
            education: "",
            occupation: "",
            workInfo: "",
            annualSalary: "",
            contactNo: "",
            email: "",
            relationshipDate: "",
            anniversaryRemarks: "",
          },
        }));
      } else {
        // Convert date from dd/MM/yyyy to yyyy-MM-dd for date input
        const convertDateForInput = (dateStr) => {
          if (!dateStr || !dateStr.includes("/")) return "";
          const [day, month, year] = dateStr.split("/");
          return `${year}-${month.padStart(2, "0")}-${day.padStart(2, "0")}`;
        };

        setFormData((prev) => ({
          ...prev,
          [type]: {
            name: member.mn || "",
            gender: member.gid || "",
            education: member.ed || "",
            occupation: member.oc || "",
            workInfo: member.wi || "",
            annualSalary: member.asl || "",
            contactNo: member.cn || "",
            email: member.em || "",
            relationshipDate: convertDateForInput(member.rdt) || "",
            anniversaryRemarks: member.arm || "",
          },
        }));
      }

      setEditMode((prev) => ({ ...prev, [type]: true }));
    } catch (error) {
      safeToast.error("Unable to load dropdowns.");
    }
  };

  const handleCancel = (type) => {
    setEditMode((prev) => ({ ...prev, [type]: false }));
  };

  const handleChange = (type, field, value) => {
    setFormData((prev) => ({
      ...prev,
      [type]: {
        ...prev[type],
        [field]: value,
      },
    }));
  };

  const handleSave = async (type) => {
    try {
      const userDetails = localStorage.getItem("UserLogDetails");
      if (!userDetails) throw new Error("User not logged in");

      const decryptedUser = decryptText(userDetails);
      const parsedUser = JSON.parse(decryptedUser);
      const { Token, StudentId, LoginId } = parsedUser;

      const memberFormData = formData[type];
      if (!memberFormData) {
        safeToast.error("No changes to save.");
        return;
      }

      // Convert date from yyyy-MM-dd to dd/MM/yyyy format if provided
      const convertDateFormat = (dateStr) => {
        if (!dateStr) return null;
        // If already in dd/MM/yyyy format, return as is
        if (dateStr.includes("/")) return dateStr;
        // Convert from yyyy-MM-dd to dd/MM/yyyy
        if (dateStr.includes("-")) {
          const [year, month, day] = dateStr.split("-");
          return `${day}/${month}/${year}`;
        }
        // Try to parse as date object if format is unknown
        const date = new Date(dateStr);
        if (isNaN(date.getTime())) return null; // Return null if invalid
        const day = String(date.getDate()).padStart(2, "0");
        const month = String(date.getMonth() + 1).padStart(2, "0");
        const year = date.getFullYear();
        return `${day}/${month}/${year}`;
      };

      const payload = {
        studentId: parseInt(StudentId),
        name: memberFormData.name || null,
        gender: memberFormData.gender ? parseInt(memberFormData.gender) : null,
        education: memberFormData.education || null,
        occupation: memberFormData.occupation || null,
        workInfo: memberFormData.workInfo || null,
        annualSalary: memberFormData.annualSalary ? parseFloat(memberFormData.annualSalary) : null,
        contactNo: memberFormData.contactNo || null,
        email: memberFormData.email || null,
        relationshipDate: convertDateFormat(memberFormData.relationshipDate),
        anniversaryRemarks: memberFormData.anniversaryRemarks || null,
        createdUserId: parseInt(StudentId),
        loginId: LoginId || 0,
      };

      const endpoints = {
        father: API_ENDPOINTS.PROFILE.UPDATE_FATHER_INFO,
        mother: API_ENDPOINTS.PROFILE.UPDATE_MOTHER_INFO,
        spouse: API_ENDPOINTS.PROFILE.UPDATE_SPOUSE_INFO,
        guardian: API_ENDPOINTS.PROFILE.UPDATE_GUARDIAN_INFO,
      };

      const response = await axios.post(
        endpoints[type],
        payload,
        {
          headers: {
            "Content-Type": "application/json",
            Authorization: `Bearer ${Token}`,
          },
        }
      );

      if (response.status === 200) {
        safeToast.success(`${type.charAt(0).toUpperCase() + type.slice(1)} information updated successfully!`);

        // Refetch the updated family info
        try {
          const refreshResponse = await axios.get(
            API_ENDPOINTS.PROFILE.GET_FAMILY_MEMBERS(StudentId),
            {
              headers: {
                "Content-Type": "application/json",
                Authorization: `Bearer ${Token}`,
              },
            }
          );

          const organized = {
            father: refreshResponse.data.find((m) => m.mnid === 0) || null,
            mother: refreshResponse.data.find((m) => m.mnid === 1) || null,
            spouse: refreshResponse.data.find((m) => m.mnid === 3) || null,
            guardian: refreshResponse.data.find((m) => m.mnid === 2) || null,
          };

          setFamilyInfo(organized);
        } catch (refreshError) {
          console.error("Error refreshing family info:", refreshError);
        }

        handleCancel(type);
      }
    } catch (error) {
      console.error("Error updating family info:", error);
      if (error.response?.status === 401) {
        safeToast.error("Session expired. Please log in again.");
        localStorage.clear();
        navigate("/BasicAuth");
      } else {
        safeToast.error(`Failed to update ${type} information.`);
      }
    }
  };

  const renderFamilyMemberSection = (type, title, icon) => {
    const member = familyInfo[type];
    const isEditing = editMode[type];
    const memberFormData = formData[type] || {};

    const getGenderName = (genderId) => {
      const gender = dropdowns.genders.find((g) => g.id === genderId);
      return gender ? (gender.ftxt || gender.txt) : "";
    };

    const fields = [
      { label: "Name", key: "mn", field: "name", editable: true },
      { label: "Gender", key: "gn", field: "gender", editable: true, isDropdown: true },
      { label: "Education", key: "ed", field: "education", editable: true },
      { label: "Occupation", key: "oc", field: "occupation", editable: true },
      { label: "Work Info", key: "wi", field: "workInfo", editable: true },
      { label: "Annual Salary", key: "asl", field: "annualSalary", editable: true },
      { label: "Contact No", key: "cn", field: "contactNo", editable: true },
      { label: "Email", key: "em", field: "email", editable: true },
      { label: "Relationship Date", key: "rdt", field: "relationshipDate", editable: true },
      { label: "Remarks", key: "arm", field: "anniversaryRemarks", editable: true },
    ];

    return (
      <Accordion defaultExpanded={!!member} sx={{ mb: 2 }}>
        <AccordionSummary expandIcon={<ExpandMoreIcon />}>
          <MDBox display="flex" alignItems="center" justifyContent="space-between" width="100%">
            <MDBox display="flex" alignItems="center">
              <Icon sx={{ mr: 1 }}>{icon}</Icon>
              <MDTypography variant="h6" color="primary">
                {title}
              </MDTypography>
            </MDBox>
            {!isEditing && (
              <Tooltip title={`Edit ${title} Information`}>
                <MDButton
                  variant="outlined"
                  color="info"
                  size="small"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleEdit(type);
                  }}
                >
                  <Icon fontSize="small">edit</Icon>
                </MDButton>
              </Tooltip>
            )}
          </MDBox>
        </AccordionSummary>
        <AccordionDetails>
          {!member && !isEditing ? (
            <MDBox textAlign="center" py={3}>
              <MDTypography variant="body2" color="text">
                No {title.toLowerCase()} information available.
              </MDTypography>
              <MDButton
                variant="contained"
                color="info"
                size="small"
                onClick={() => handleEdit(type)}
                sx={{ mt: 2 }}
              >
                Add {title} Information
              </MDButton>
            </MDBox>
          ) : (
            <Grid container spacing={2}>
              {fields.map((item, index) => (
                <Grid item xs={12} sm={6} key={index}>
                  <MDBox mb={2}>
                    <InputLabel htmlFor={`${type}-${item.field}`} sx={{ fontWeight: "bold", mb: 1 }}>
                      {item.label}
                    </InputLabel>
                    {isEditing ? (
                      item.isDropdown ? (
                        <TextField
                          id={`${type}-${item.field}`}
                          name={item.field}
                          select
                          SelectProps={{ native: true }}
                          value={memberFormData[item.field] || ""}
                          onChange={(e) => handleChange(type, item.field, e.target.value)}
                          fullWidth
                          size="small"
                          variant="outlined"
                        >
                          <option value="">Select Gender</option>
                          {dropdowns.genders.map((gender) => (
                            <option key={gender.id} value={gender.id}>
                              {gender.ftxt || gender.txt}
                            </option>
                          ))}
                        </TextField>
                      ) : item.field === "workInfo" || item.field === "anniversaryRemarks" ? (
                        <TextField
                          id={`${type}-${item.field}`}
                          name={item.field}
                          value={memberFormData[item.field] || ""}
                          onChange={(e) => handleChange(type, item.field, e.target.value)}
                          fullWidth
                          size="small"
                          variant="outlined"
                          multiline
                          rows={2}
                        />
                      ) : (
                        <TextField
                          id={`${type}-${item.field}`}
                          name={item.field}
                          type={
                            item.field === "annualSalary"
                              ? "number"
                              : item.field === "email"
                              ? "email"
                              : item.field === "relationshipDate"
                              ? "date"
                              : "text"
                          }
                          value={memberFormData[item.field] || ""}
                          onChange={(e) => {
                            // For date inputs, keep the value in yyyy-MM-dd format
                            handleChange(type, item.field, e.target.value);
                          }}
                          fullWidth
                          size="small"
                          variant="outlined"
                        />
                      )
                    ) : (
                      <MDTypography variant="body2" fontWeight="regular">
                        {item.field === "gender" && member?.gid
                          ? getGenderName(member.gid) || member[item.key] || "-"
                          : member?.[item.key] || "-"}
                      </MDTypography>
                    )}
                  </MDBox>
                </Grid>
              ))}
            </Grid>
          )}

          {isEditing && (
            <MDBox display="flex" justifyContent="flex-end" mt={3}>
              <MDButton variant="contained" color="info" onClick={() => handleSave(type)} sx={{ mr: 1 }}>
                Save
              </MDButton>
              <MDButton variant="outlined" color="secondary" onClick={() => handleCancel(type)}>
                Cancel
              </MDButton>
            </MDBox>
          )}
        </AccordionDetails>
      </Accordion>
    );
  };

  if (loading) {
    return (
      <MDBox display="flex" justifyContent="center" py={3}>
        <CircularProgress />
      </MDBox>
    );
  }

  return (
    <MDBox py={2} px={3}>
      <Card sx={{ boxShadow: 3, borderRadius: 2 }}>
        <MDBox
          variant="gradient"
          bgColor="info"
          borderRadius="lg"
          coloredShadow="info"
          mx={2}
          mt={-3}
          p={2}
          mb={1}
          display="flex"
          alignItems="center"
          justifyContent="space-between"
        >
          <MDTypography variant="h6" color="white">
            Family Information
          </MDTypography>
        </MDBox>

        <CardContent sx={{ backgroundColor: "#fafafa" }}>
          {renderFamilyMemberSection("father", "Father", "person")}
          {renderFamilyMemberSection("mother", "Mother", "person")}
          {renderFamilyMemberSection("spouse", "Spouse", "favorite")}
          {renderFamilyMemberSection("guardian", "Guardian", "security")}
        </CardContent>
      </Card>
    </MDBox>
  );
};

export default FamilyInfo;
